import { escapeHtml } from "../util/escape-html.js";
/**
 * Base element for the 5 skinned "owner stages" (home/wallet/friends/agents/profile) — the
 * built-in, kernel-native dashboards rendered directly by the shell (as opposed to the
 * generic `StageLoader`-mounted, dynamically-installed stage apps). Subclasses port the
 * ORIGINAL skin's per-stage DOM into `renderView`, keyed off a single kernel `liveQuery`
 * (or `query === undefined` when the backing domain isn't wired yet, per the data policy:
 * real kernel RPC where the domain exists, a skinned "Coming soon" panel otherwise — never a
 * fabricated data path). Lifecycle (subscribe on connect / unsubscribe on disconnect,
 * generation-guarded against races) mirrors the existing `OwnerViewStageElement`.
 *
 * Rail slot: the original skin puts some stages' list/nav content in the shell's `.rail`
 * sidebar (a sibling of `.main`, not a descendant — see `<aside class="rail" data-rail>` in
 * `app-shell.ts`), keyed by `.rail-view[data-view="…"]` (see `.shell[data-view="…"]
 * .rail-view[data-view="…"]{display:grid}` in `skin/css/base.css`, and the auto collapse/
 * expand `:has(.rail-view)` rule in `skin/skin-stylesheet.ts`). `renderView` alone can only
 * reach the stage's own subtree (appended under `[data-stage-outlet]`, inside `.main`), so it
 * cannot place anything in `.rail`. Subclasses that own rail content override `renderRail`
 * instead; the base class locates the shell's real `[data-rail]` container (via
 * `getRootNode()`, since the stage element and the rail live in the same shadow root/document)
 * and keeps a single owned slot inside it in sync with the query lifecycle. Default is a
 * no-op — most stages render only into `.main` and never touch the rail.
 */
export class SkinnedOwnerStageElement extends HTMLElement {
    #client;
    #generation = 0;
    #rows = new Map();
    #unsubscribe;
    #railSlot;
    /**
     * Optional hook: populate `root` (an element the base class owns and keeps mounted inside
     * the shell's `[data-rail]` aside) with rail content for the given rows. Called whenever
     * `renderView` is — i.e. only in the "ready" (rows.length > 0) state; the rail slot is
     * cleared in every other lifecycle state (loading/empty/error/coming-soon/disconnected), the
     * same way the pre-relocation inline markup disappeared under those states. No-op by
     * default.
     */
    renderRail(_root, _rows) {
        // Most owner stages don't render anything into the shell's `.rail`.
    }
    configure(options) {
        this.#client = options.client;
        if (this.isConnected)
            this.#start();
    }
    connectedCallback() {
        this.#start();
    }
    disconnectedCallback() {
        this.#generation += 1;
        this.#unsubscribe?.();
        this.#unsubscribe = undefined;
        this.#clearRailSlot();
    }
    #start() {
        this.#unsubscribe?.();
        this.#unsubscribe = undefined;
        this.#rows.clear();
        const query = this.query;
        if (query === undefined) {
            this.#renderComingSoon();
            return;
        }
        this.#renderLoading();
        const client = this.#client;
        if (client === undefined) {
            this.#renderError();
            return;
        }
        const generation = ++this.#generation;
        this.#unsubscribe = client.liveQuery(query, {}, (diff) => {
            if (generation !== this.#generation)
                return;
            this.#applyDiff(diff);
            this.#renderRows();
        });
        void this.#load(generation, query, client);
    }
    async #load(generation, query, client) {
        try {
            const rows = await client.query(query, {});
            if (generation !== this.#generation)
                return;
            this.#replace(rows);
            this.#renderRows();
        }
        catch (error) {
            if (generation === this.#generation)
                this.#renderError(error);
        }
    }
    #applyDiff(diff) {
        for (const row of diff.deleted)
            this.#rows.delete(rowKey(row));
        for (const row of [...diff.updated, ...diff.added])
            this.#rows.set(rowKey(row), row);
    }
    #replace(rows) {
        this.#rows.clear();
        for (const row of rows)
            this.#rows.set(rowKey(row), row);
    }
    #renderLoading() {
        this.dataset.stageState = "loading";
        this.setAttribute("aria-busy", "true");
        this.innerHTML = `<div class="ui-card" role="status"><p class="ui-eyebrow">Loading</p></div>`;
        this.#clearRailSlot();
    }
    #renderRows() {
        this.removeAttribute("aria-busy");
        const rows = [...this.#rows.values()];
        if (rows.length === 0) {
            this.#renderEmpty();
            return;
        }
        this.dataset.stageState = "ready";
        this.innerHTML = "";
        this.renderView(this, rows);
        this.#syncRailSlot(rows);
    }
    #renderEmpty() {
        this.dataset.stageState = "empty";
        this.innerHTML = `<div class="ui-card"><p class="ui-eyebrow">Nothing yet</p><p>${escapeHtml(this.emptyMessage)}</p></div>`;
        this.#clearRailSlot();
    }
    /**
     * #1661 — say WHY, when the kernel told us why.
     *
     * A view backed by the durable local cache fails with a `DomainError` whose message
     * names the actual `StorageRecoveryState` reason ("Durable local storage is
     * unavailable: Persistent browser storage is unavailable" on a browser with no OPFS).
     * Collapsing that into "This view is unavailable right now" is exactly the generic
     * dead-end this issue is about, so the specific reason is surfaced when present and
     * the generic line is kept only as the genuine fallback for an unlabelled failure.
     */
    #renderError(error) {
        this.removeAttribute("aria-busy");
        this.dataset.stageState = "error";
        const reason = describeStageError(error);
        this.innerHTML = `<div class="ui-card" role="alert"><p class="ui-eyebrow">Unavailable</p>`
            + `<p>${escapeHtml(reason)}</p></div>`;
        this.#clearRailSlot();
    }
    #renderComingSoon() {
        this.removeAttribute("aria-busy");
        this.dataset.stageState = "coming-soon";
        this.innerHTML = `<div class="ui-card"><p class="ui-eyebrow">Coming soon</p><p>${escapeHtml(this.emptyMessage)}</p></div>`;
        this.#clearRailSlot();
    }
    /**
     * Locate the shell's real rail container. The stage element is appended under
     * `[data-stage-outlet]` inside `.main`; `.rail` (`[data-rail]`) is a sibling reachable only
     * via the shared root — `getRootNode()` returns the enclosing `ShadowRoot` once connected
     * inside `<node-app-shell>` (or `Document` in a plain test harness / when no shell wraps
     * this stage), and `[data-rail]` may legitimately not exist in either case.
     */
    #railContainer() {
        const root = this.getRootNode();
        if (root instanceof ShadowRoot || root instanceof Document) {
            return root.querySelector("[data-rail]");
        }
        return null;
    }
    #syncRailSlot(rows) {
        const rail = this.#railContainer();
        if (!rail) {
            this.#clearRailSlot();
            return;
        }
        if (!this.#railSlot || this.#railSlot.parentElement !== rail) {
            this.#railSlot?.remove();
            const slot = document.createElement("div");
            // Transparent to layout: `.rail-view` (and any sibling content a stage renders here)
            // becomes a real box-tree child of `.rail` itself, matching what the vendored
            // `.rail-view[data-view="…"]`/`:has(.rail-view)` rules assume.
            slot.style.display = "contents";
            slot.dataset.ownerStageRail = "";
            rail.append(slot);
            this.#railSlot = slot;
        }
        this.#railSlot.innerHTML = "";
        this.renderRail(this.#railSlot, rows);
        if (this.#railSlot.innerHTML.trim() === "") {
            this.#clearRailSlot();
        }
    }
    #clearRailSlot() {
        this.#railSlot?.remove();
        this.#railSlot = undefined;
    }
}
const GENERIC_STAGE_ERROR = "This view is unavailable right now.";
/**
 * The kernel's own explanation when it gave one, else the generic line. A bare `Error`
 * with an empty message, a thrown non-Error, or no error at all all fall back — a blank
 * or `"[object Object]"` panel would be worse than the generic sentence.
 */
function describeStageError(error) {
    if (!(error instanceof Error))
        return GENERIC_STAGE_ERROR;
    const message = error.message.trim();
    return message === "" ? GENERIC_STAGE_ERROR : message;
}
function rowKey(row) {
    for (const candidate of [row.id, row.homeNodeId, row.key]) {
        if (typeof candidate === "string" && candidate.length > 0)
            return candidate;
    }
    return JSON.stringify(row);
}
//# sourceMappingURL=skinned-owner-stage.js.map